// Blog renderer: "Article" shows the post as a page on the brand's own site, styled only from the
// brand visual kit (palette roles, heading/body/mono fonts, wordmark, eyebrow) with neutral
// defaults; "Shared link" shows the OG card inside a neutral link-unfurl mock.
// The body uses markdownToHtml from shared/blog-render.ts, the same renderer as the handoff export.
import { useMemo } from "react";
import { markdownToHtml } from "../../../../../shared/blog-render";
import { pickMedia, type PreviewDevice, type PreviewModel } from "../../../../../shared/content-preview";
import { fontStack, sitePalette } from "../kit-style";
import { useKitFonts } from "../kit-fonts";
import { formatPreviewDate } from "../format";
import { LinkUnfurlCard } from "./LinkUnfurlCard";

/** Typography for the rendered body, scoped to `.hc-site-article` (reads the CSS variables set below). */
const ARTICLE_CSS = `
.hc-site-article .hc-site-body{font-family:var(--site-body);font-size:1.125rem;line-height:1.75;color:var(--site-text)}
.hc-site-article .hc-site-body>*+*{margin-top:1.25em}
.hc-site-article .hc-site-body h1,.hc-site-article .hc-site-body h2,.hc-site-article .hc-site-body h3,.hc-site-article .hc-site-body h4{font-family:var(--site-heading);line-height:1.2;margin-top:1.8em;color:var(--site-text)}
.hc-site-article .hc-site-body h2{font-size:1.875rem}
.hc-site-article .hc-site-body h3{font-size:1.5rem}
.hc-site-article .hc-site-body a{color:var(--site-accent);text-decoration:underline;text-underline-offset:3px}
.hc-site-article .hc-site-body ul{list-style:disc;padding-left:1.5em}
.hc-site-article .hc-site-body ol{list-style:decimal;padding-left:1.5em}
.hc-site-article .hc-site-body li+li{margin-top:.4em}
.hc-site-article .hc-site-body blockquote{border-left:3px solid var(--site-accent);padding-left:1em;color:var(--site-muted);font-style:italic}
.hc-site-article .hc-site-body code{font-family:var(--site-mono);font-size:.9em;background:var(--site-surface);padding:.1em .35em;border-radius:4px}
.hc-site-article .hc-site-body pre{font-family:var(--site-mono);background:var(--site-surface);padding:1em;border-radius:8px;overflow-x:auto;font-size:.875rem;line-height:1.6}
.hc-site-article .hc-site-body pre code{background:none;padding:0}
.hc-site-article .hc-site-body img{max-width:100%;height:auto;border-radius:12px}
.hc-site-article.is-mobile .hc-site-body{font-size:1.0625rem}
.hc-site-article.is-mobile .hc-site-body h2{font-size:1.5rem}
`;

export function BlogArticle({ model, device }: { model: PreviewModel; device: PreviewDevice }) {
  const { identity } = model;
  const palette = sitePalette(identity);
  const fontsActive = useKitFonts(identity.font_files);
  const bodyHtml = useMemo(() => markdownToHtml(model.body), [model.body]);
  const hero = pickMedia(model, ["hero"]);
  const mobile = device === "mobile";
  const date = formatPreviewDate(model.date);
  const vars = {
    "--site-heading": fontStack(identity, "heading"),
    "--site-body": fontStack(identity, "body"),
    "--site-mono": fontStack(identity, "mono"),
    "--site-text": palette.text,
    "--site-muted": palette.muted,
    "--site-accent": palette.accent,
    "--site-surface": palette.surface,
    background: palette.background,
    color: palette.text,
    fontFamily: "var(--site-body)",
  } as React.CSSProperties;

  return (
    <div className={`hc-site-article min-h-full ${mobile ? "is-mobile" : ""}`} style={vars} data-testid="preview-blog-article" data-has-kit={identity.has_kit ? "true" : "false"} data-kit-fonts={fontsActive}>
      <style>{ARTICLE_CSS}</style>
      <header className={`flex items-center justify-between gap-4 border-b ${mobile ? "px-4 py-3" : "px-8 py-4"}`} style={{ borderColor: `${palette.muted}33` }}>
        <span className="flex items-center gap-2 text-xl font-semibold" style={{ fontFamily: "var(--site-heading)" }} data-testid="preview-site-wordmark">
          {identity.logo_url && <img src={identity.logo_url} alt="" className="h-7 w-7 rounded object-contain" />}
          {identity.wordmark}
        </span>
        <nav aria-label="Site (preview)" className="flex gap-4 text-sm" style={{ color: palette.muted }}>
          <span style={{ color: palette.text }}>Blog</span>
          {!mobile && <span>About</span>}
        </nav>
      </header>
      <article className={`mx-auto w-full ${mobile ? "px-4 py-6" : "px-8 py-10"}`} style={{ maxWidth: 728 }}>
        <span className="text-sm hover:underline" style={{ color: palette.accent }}>← All posts</span>
        {identity.eyebrow && (
          <p className="mt-6 text-xs font-semibold uppercase tracking-widest" style={{ color: palette.accent }} data-testid="preview-eyebrow">{identity.eyebrow}</p>
        )}
        <h1
          className={identity.eyebrow ? "mt-2" : "mt-6"}
          style={{ fontFamily: "var(--site-heading)", fontSize: mobile ? "2.25rem" : "3rem", lineHeight: 1.1, color: palette.text }}
          data-testid="preview-title"
        >
          {model.title}
        </h1>
        {date && <p className="mt-3 text-sm" style={{ color: palette.muted }} data-testid="preview-date">{date}</p>}
        {hero ? (
          <img src={hero.url} alt={hero.alt} className="mt-8 aspect-[16/9] w-full rounded-xl object-cover" data-testid="preview-article-hero" />
        ) : (
          <div className="mt-8 flex aspect-[16/9] w-full items-center justify-center rounded-xl border border-dashed text-sm"
            style={{ borderColor: `${palette.muted}66`, color: palette.muted, background: palette.surface }} data-testid="preview-article-hero-missing">
            Hero image goes here
          </div>
        )}
        {model.body ? (
          // markdownToHtml escapes all text and only keeps http(s)/mailto/site-relative URLs.
          <div className="hc-site-body mt-10" data-testid="preview-body" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
        ) : (
          <p className="mt-10" style={{ color: palette.muted }} data-testid="preview-body-empty">No draft yet.</p>
        )}
      </article>
      <footer className={`border-t text-sm ${mobile ? "px-4 py-6" : "px-8 py-8"}`} style={{ borderColor: `${palette.muted}33`, color: palette.muted }}>
        © {new Date().getFullYear()} {identity.name}{identity.domain ? ` · ${identity.domain}` : ""}
      </footer>
    </div>
  );
}

export function BlogSharedLink({ model, device }: { model: PreviewModel; device: PreviewDevice }) {
  return (
    <div className={`min-h-full bg-slate-100 ${device === "mobile" ? "p-3" : "p-8"}`} data-testid="preview-blog-shared-link">
      <div className="mx-auto max-w-xl space-y-2">
        <p className="text-xs text-slate-500">How a link to this post unfurls in a feed or chat (generic, not a specific platform).</p>
        <div className="rounded-2xl bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <span aria-hidden className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-600">{model.identity.name.slice(0, 1).toUpperCase()}</span>
            <span className="text-sm font-medium text-slate-800">{model.identity.name}</span>
          </div>
          <p className="mb-3 text-sm text-slate-700">New on the blog: {model.title}</p>
          <LinkUnfurlCard link={model.link} />
        </div>
      </div>
    </div>
  );
}
